import { useState, useEffect } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import {
  CAPTION_PRESETS,
  CAPTION_PRESET_NAMES,
  CAPTION_PRESET_LABELS,
  CAPTION_PRESET_DESCRIPTIONS,
  DEFAULT_CAPTION_PRESET,
  type CaptionPresetConfig,
} from '@/lib/captionPresets'
import { type TextOverlay } from '@/lib/clipUtils'
import { cn } from '@/lib/utils'
import { Captions, Check, Loader2 } from 'lucide-react'

const CAPTION_FONT_URL = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@700;800&family=Oswald:wght@600;700&family=Poppins:wght@700&family=Raleway:wght@800&family=Roboto+Mono:wght@500&family=Bangers&family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&family=Bebas+Neue&family=Libre+Bodoni:ital,wght@0,400;0,700;1,400&family=Barlow+Condensed:wght@400;600;700;800&display=swap'

const FONT_FAMILY_CSS: Record<string, string> = {
  Inter: 'Inter, sans-serif',
  Montserrat: '"Montserrat", sans-serif',
  Oswald: '"Oswald", sans-serif',
  Poppins: '"Poppins", sans-serif',
  Raleway: '"Raleway", sans-serif',
  RobotoMono: '"Roboto Mono", monospace',
  Bangers: '"Bangers", cursive',
  Outfit: '"Outfit", sans-serif',
  'JetBrains Mono': '"JetBrains Mono", monospace',
  'Bebas Neue': '"Bebas Neue", sans-serif',
  'Libre Bodoni': '"Libre Bodoni", serif',
  'Barlow Condensed': '"Barlow Condensed", sans-serif',
}

/**
 * Loads the Google Fonts used by caption previews (once per page). Call from
 * any component that renders CaptionSampleBox.
 */
export function useCaptionPreviewFonts() {
  useEffect(() => {
    const id = 'caption-preview-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = CAPTION_FONT_URL
    document.head.appendChild(link)
  }, [])
}

/**
 * A live caption sample rendered in a preset's exact style — shared by the
 * Caption Style panel and the agent's question pickers / treatment board.
 */
export function CaptionSampleBox({
  presetName,
  fontSize = 13,
  words = ['Hello', 'World'],
}: {
  presetName: string
  fontSize?: number
  words?: [string, string]
}) {
  const config: CaptionPresetConfig = CAPTION_PRESETS[presetName] || CAPTION_PRESETS[DEFAULT_CAPTION_PRESET]

  const previewBg = config.background === 'transparent'
    ? 'transparent'
    : config.background

  const isOutline = config.wordAnimation === 'fill'
  const isSolo = config.wordAnimation === 'solo'

  const previewStyle: React.CSSProperties = {
    fontSize,
    fontWeight: config.fontWeight,
    fontFamily: FONT_FAMILY_CSS[config.fontFamily] || config.fontFamily,
    color: isOutline ? 'transparent' : (config.color === 'transparent' ? config.highlightColor : config.color),
    textShadow: config.shadow && config.shadow !== 'none' ? config.shadow : undefined,
    letterSpacing: config.letterSpacing,
    lineHeight: 1.3,
  }

  if (config.glow) {
    const baseShadow = config.shadow && config.shadow !== 'none' ? config.shadow : ''
    previewStyle.textShadow = [baseShadow, config.glow].filter(Boolean).join(', ')
  }

  if (isOutline && config.stroke) {
    previewStyle.WebkitTextStroke = `${config.strokeWidth || 2}px ${config.stroke}`
  } else if (config.stroke && config.wordAnimation !== 'fill') {
    previewStyle.WebkitTextStroke = `${Math.min(config.strokeWidth || 2, 1)}px ${config.stroke}`
  }

  return (
    <div
      className="rounded px-2 py-1 max-w-full truncate"
      style={{
        backgroundColor: previewBg,
        ...previewStyle,
      }}
    >
      {isSolo ? (
        <span style={{ color: config.highlightColor }}>{words[0]}</span>
      ) : (
        <><span style={{ color: config.highlightColor }}>{words[0]}</span>{' '}
        <span style={{ opacity: 0.5 }}>{words[1]}</span></>
      )}
    </div>
  )
}

function PresetPreviewCard({
  name,
  isActive,
  isApplying,
  onClick,
}: {
  name: string
  isActive: boolean
  isApplying: boolean
  onClick: () => void
}) {
  const label = CAPTION_PRESET_LABELS[name] || name

  return (
    <button
      onClick={onClick}
      aria-label={`Apply ${label} caption style`}
      aria-pressed={isActive}
      className={cn(
        'relative rounded-lg overflow-hidden text-left transition-all w-full',
        'border hover:border-primary',
        isActive ? 'border-primary ring-1 ring-primary/30' : 'border-neutral-700',
      )}
    >
      <div className="bg-neutral-900 p-2 h-[58px] flex items-center justify-center">
        <CaptionSampleBox presetName={name} />
      </div>
      <div className="px-2 py-1.5 bg-neutral-800">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-neutral-200 truncate">{label}</span>
          {isApplying ? (
            <Loader2 size={12} className="text-primary animate-spin shrink-0 ml-1" />
          ) : isActive ? (
            <Check size={12} className="text-primary shrink-0 ml-1" />
          ) : null}
        </div>
        <p className="text-[10px] text-neutral-400 truncate">{CAPTION_PRESET_DESCRIPTIONS[name]}</p>
      </div>
    </button>
  )
}

export function CaptionStylePanel({
  workflowId,
  textOverlays,
}: {
  workflowId: Id<'workflows'>
  textOverlays?: TextOverlay[]
}) {
  const applyCaptionPreset = useMutation(api.workflows.applyCaptionPreset)
  const updateOverlay = useMutation(api.workflows.updateTextOverlay)
  const [applying, setApplying] = useState<string | null>(null)
  const [movingPos, setMovingPos] = useState(false)

  useCaptionPreviewFonts()

  const captionOverlays = (textOverlays || []).filter(o => o.id.startsWith('caption-'))

  // Position is independent of style — patched directly on each overlay so it
  // survives style changes and works even with per-clip mixed styles.
  const currentPosition = (captionOverlays[0]?.position as string) || 'bottom_center'
  const [position, setPosition] = useState(currentPosition)
  useEffect(() => { setPosition(currentPosition) }, [currentPosition])

  const allPresetsMatch = captionOverlays.length > 0 &&
    captionOverlays.every(o => o.preset === captionOverlays[0]?.preset)
  const currentPreset = allPresetsMatch ? (captionOverlays[0]?.preset as string | undefined) : undefined

  if (captionOverlays.length === 0) {
    return (
      <div className="p-4 text-center">
        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mx-auto mb-2">
          <Captions size={18} className="text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground">No captions yet.</p>
        <p className="text-[10px] text-muted-foreground/70 mt-1">Ask the assistant to generate captions from your clip transcriptions.</p>
      </div>
    )
  }

  const handleApply = async (presetName: string) => {
    if (applying) return
    setApplying(presetName)
    const config = CAPTION_PRESETS[presetName]
    if (!config) { setApplying(null); return }
    try {
      await applyCaptionPreset({
        workflowId,
        preset: presetName,
        config: {
          fontFamily: config.fontFamily,
          fontSize: config.fontSize,
          fontWeight: config.fontWeight,
          color: config.color,
          background: config.background,
          highlightColor: config.highlightColor,
          position, // keep the user's chosen position across style changes
          shadow: config.shadow,
          stroke: config.stroke,
          strokeWidth: config.strokeWidth,
          letterSpacing: config.letterSpacing,
          wordAnimation: config.wordAnimation,
          glow: config.glow,
        },
      })
    } finally {
      setApplying(null)
    }
  }

  const handlePosition = async (pos: string) => {
    if (movingPos || pos === position) return
    setPosition(pos)
    setMovingPos(true)
    try {
      await Promise.all(
        captionOverlays.map(o => updateOverlay({ workflowId, overlayId: o.id, position: pos }))
      )
    } finally {
      setMovingPos(false)
    }
  }

  return (
    <div className="py-3">
      <div className="flex items-center gap-2 mb-3">
        <Captions size={14} className="text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Caption Style</span>
        <span className="text-[10px] text-muted-foreground/70 ml-auto">{captionOverlays.length} captions</span>
      </div>
      <p className="text-[10px] text-muted-foreground/70 mb-2">Applies to all captions</p>

      <div className="mb-3">
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Position</div>
        <div className="grid grid-cols-3 gap-1">
          {([['top_center', 'Top'], ['center', 'Center'], ['bottom_center', 'Bottom']] as const).map(([val, label]) => (
            <button
              key={val}
              onClick={() => handlePosition(val)}
              disabled={movingPos}
              aria-pressed={position === val}
              className={cn(
                'py-1.5 text-[11px] font-medium rounded-md border transition-colors disabled:opacity-60',
                position === val
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-neutral-700 text-muted-foreground hover:text-foreground hover:border-neutral-500',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Style</div>
      <div className="grid grid-cols-2 gap-2">
        {CAPTION_PRESET_NAMES.map(name => (
          <PresetPreviewCard
            key={name}
            name={name}
            isActive={currentPreset === name}
            isApplying={applying === name}
            onClick={() => handleApply(name)}
          />
        ))}
      </div>
    </div>
  )
}
